import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const WorkspaceContext = createContext();

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return context;
};

export const WorkspaceProvider = ({ children }) => {
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(null);
  const [isCanvasOpen, setIsCanvasOpen] = useState(false);

  // Load workspaces from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('liquidtext_workspaces');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setWorkspaces(parsed);
      } catch (e) {
        console.error('Failed to load workspaces:', e);
      }
    }
  }, []);

  // Save workspaces to localStorage whenever they change
  useEffect(() => {
    if (workspaces.length > 0) {
      localStorage.setItem('liquidtext_workspaces', JSON.stringify(workspaces));
    }
  }, [workspaces]);

  // Get active workspace
  const activeWorkspace = workspaces.find(w => w.id === activeWorkspaceId);

  // Create new workspace
  const createWorkspace = useCallback((name, documents = []) => {
    const newWorkspace = {
      id: `workspace_${Date.now()}`,
      name: name || `Workspace ${workspaces.length + 1}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      documents: documents.map((doc, index) => ({
        id: doc.id || `doc_${Date.now()}_${index}`,
        name: doc.name || doc.filename,
        content: doc.content,
        position: { x: 100 + (index * 400), y: 100 },
        size: { width: 600, height: 800 },
        zoom: 1.0,
        currentPage: 1,
        rotation: 0
      })),
      excerpts: [],
      connections: [],
      annotations: [],
      canvasPosition: { x: 0, y: 0 },
      canvasZoom: 1
    };

    setWorkspaces(prev => [...prev, newWorkspace]);
    setActiveWorkspaceId(newWorkspace.id);
    setIsCanvasOpen(true);
    return newWorkspace;
  }, [workspaces.length]);

  // Update workspace
  const updateWorkspace = useCallback((workspaceId, updates) => {
    setWorkspaces(prev => prev.map(w => 
      w.id === workspaceId 
        ? { ...w, ...updates, updatedAt: new Date().toISOString() }
        : w
    ));
  }, []);

  // Delete workspace
  const deleteWorkspace = useCallback((workspaceId) => {
    setWorkspaces(prev => prev.filter(w => w.id !== workspaceId));
    if (activeWorkspaceId === workspaceId) {
      setActiveWorkspaceId(null);
      setIsCanvasOpen(false);
    }
  }, [activeWorkspaceId]);

  // Add document to workspace
  const addDocument = useCallback((workspaceId, document) => {
    setWorkspaces(prev => prev.map(w => {
      if (w.id === workspaceId) {
        const newDoc = {
          id: document.id || `doc_${Date.now()}`,
          name: document.name || document.filename,
          content: document.content,
          position: { 
            x: 100 + (w.documents.length * 400), 
            y: 100 
          },
          size: { width: 600, height: 800 },
          zoom: 1.0,
          currentPage: 1,
          rotation: 0
        };
        return {
          ...w,
          documents: [...w.documents, newDoc],
          updatedAt: new Date().toISOString()
        };
      }
      return w;
    }));
  }, []);

  // Add excerpt
  const addExcerpt = useCallback((workspaceId, excerpt) => {
    setWorkspaces(prev => prev.map(w => {
      if (w.id === workspaceId) {
        const newExcerpt = {
          id: `excerpt_${Date.now()}`,
          ...excerpt,
          createdAt: new Date().toISOString()
        };
        return {
          ...w,
          excerpts: [...w.excerpts, newExcerpt],
          updatedAt: new Date().toISOString()
        };
      }
      return w;
    }));
  }, []);

  // Add connection
  const addConnection = useCallback((workspaceId, connection) => {
    setWorkspaces(prev => prev.map(w => {
      if (w.id === workspaceId) {
        const newConnection = {
          id: `conn_${Date.now()}`,
          ...connection,
          createdAt: new Date().toISOString()
        };
        return {
          ...w,
          connections: [...w.connections, newConnection],
          updatedAt: new Date().toISOString()
        };
      }
      return w;
    }));
  }, []);

  // Add annotation
  const addAnnotation = useCallback((workspaceId, annotation) => {
    setWorkspaces(prev => prev.map(w => {
      if (w.id === workspaceId) {
        const newAnnotation = {
          id: `annot_${Date.now()}`,
          ...annotation,
          createdAt: new Date().toISOString()
        };
        return {
          ...w,
          annotations: [...w.annotations, newAnnotation],
          updatedAt: new Date().toISOString()
        };
      }
      return w;
    }));
  }, []);

  // Open workspace in canvas
  const openWorkspace = useCallback((workspaceId) => {
    setActiveWorkspaceId(workspaceId);
    setIsCanvasOpen(true);
  }, []);

  // Close canvas
  const closeCanvas = useCallback(() => {
    setIsCanvasOpen(false);
  }, []);

  // Export workspace
  const exportWorkspace = useCallback((workspaceId, format = 'json') => {
    const workspace = workspaces.find(w => w.id === workspaceId);
    if (!workspace) return null;

    if (format === 'json') {
      const dataStr = JSON.stringify(workspace, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
      const exportFileDefaultName = `${workspace.name.replace(/\s+/g, '_')}_workspace.json`;
      
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
    }
    
    return workspace;
  }, [workspaces]);

  const value = {
    workspaces,
    activeWorkspace,
    activeWorkspaceId,
    isCanvasOpen,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    addDocument,
    addExcerpt,
    addConnection,
    addAnnotation,
    openWorkspace,
    closeCanvas,
    exportWorkspace
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};
